// MarketingDashboard.jsx
// Replaces the demo Marketing component with live attribution data
// Import this in your main CRM file and swap it in renderPage()

import React, { useState, useEffect } from 'react';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

const MarketingDashboard = ({ leads }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState(90);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [gbpStatus, setGbpStatus] = useState(null);
  const [gbpReviews, setGbpReviews] = useState(null);
  const [gbpInsights, setGbpInsights] = useState(null);
  const [gbpPosts, setGbpPosts] = useState(null);
  const [gbpLoading, setGbpLoading] = useState(false);
  const [replyDraft, setReplyDraft] = useState({});
  const [generatingReply, setGeneratingReply] = useState(null);
  const [gbpTab, setGbpTab] = useState('reviews');

  // Fetch attribution data
  useEffect(() => {
    fetchAttribution();
    fetchGoogleStatus();
    fetchGBPStatus();
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAttribution = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/attribution?period=${period}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const fetchGoogleStatus = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/google-ads?action=status`);
      const json = await res.json();
      setGoogleStatus(json);
    } catch (e) { /* ignore */ }
  };

  const fetchGBPStatus = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=status`);
      const json = await res.json();
      setGbpStatus(json);
    } catch (e) { /* ignore */ }
  };

  const fetchGBPReviews = async () => {
    setGbpLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=reviews&pageSize=50`);
      const json = await res.json();
      setGbpReviews(json);
    } catch (e) { console.log('GBP reviews error:', e.message); }
    setGbpLoading(false);
  };

  const fetchGBPInsights = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=insights&period=${period}`);
      const json = await res.json();
      setGbpInsights(json);
    } catch (e) { /* ignore */ }
  };

  const fetchGBPPosts = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=posts`);
      const json = await res.json();
      setGbpPosts(json);
    } catch (e) { /* ignore */ }
  };

  const handleGBPConnect = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=auth-url`);
      const data = await res.json();
      if (data.authUrl) window.location.href = data.authUrl;
    } catch (e) { alert('Error: ' + e.message); }
  };

  const generateReviewReply = async (review) => {
    setGeneratingReply(review.reviewId || review.google_review_id);
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=generate-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewerName: review.reviewer || review.reviewer_name,
          rating: review.rating,
          comment: review.comment,
        }),
      });
      const data = await res.json();
      if (data.reply) {
        setReplyDraft(prev => ({ ...prev, [review.reviewId || review.google_review_id]: data.reply }));
      }
    } catch (e) { alert('AI reply generation failed: ' + e.message); }
    setGeneratingReply(null);
  };

  const submitReviewReply = async (reviewId, comment) => {
    try {
      const res = await fetch(`${BASE_URL}/api/marketing/gbp?action=reply-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewId, comment }),
      });
      if (res.ok) {
        setReplyDraft(prev => { const n = { ...prev }; delete n[reviewId]; return n; });
        fetchGBPReviews();
        alert('Reply posted to Google!');
      } else {
        const err = await res.json();
        alert('Reply failed: ' + (err.error || 'Unknown error'));
      }
    } catch (e) { alert('Reply failed: ' + e.message); }
  };

  // Load GBP data when switching to the GBP tab
  useEffect(() => {
    if (activeTab === 'gbp' && gbpStatus?.connected) {
      if (!gbpReviews) fetchGBPReviews();
      if (!gbpInsights) fetchGBPInsights();
      if (!gbpPosts) fetchGBPPosts();
    }
  }, [activeTab, gbpStatus?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = n => `$${Number(n || 0).toLocaleString()}`;
  const pct = n => `${Number(n || 0)}%`;

  const SOURCE_COLORS = {
    Google: '#4285f4', Facebook: '#1877f2', Instagram: '#e1306c',
    Referral: '#c9a96e', Direct: '#6b8cae', Website: '#4a7c59',
    'Website Contact Form': '#4a7c59', Yelp: '#d32323', Nextdoor: '#8bc34a',
    Unknown: '#8c7b6e',
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'sources', label: 'Lead Sources', icon: '🔗' },
    { id: 'trends', label: 'Trends', icon: '📈' },
    { id: 'projects', label: 'Project Types', icon: '🏠' },
    { id: 'google', label: 'Google Ads', icon: '🔍' },
    { id: 'gbp', label: 'Google Business', icon: '📍' },
  ];

  if (loading && !data) {
    return (
      <div className="fade-in" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <h1 className="section-title">Marketing Center</h1>
        <p className="section-sub">Loading live data...</p>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--stone-300)', borderTopColor: 'var(--warm-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ fontSize: 13, color: 'var(--stone-400)' }}>Fetching attribution data...</div>
        </div>
      </div>
    );
  }

  const funnel = data?.funnel || {};
  const sources = data?.sources || [];
  const revenue = data?.revenue || {};
  const calls = data?.calls || {};
  const trends = data?.weeklyTrend || [];
  const projects = data?.topProjects || [];

  return (
    <div className="fade-in" style={{ minHeight: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="section-title">Marketing Center</h1>
          <p className="section-sub">Live lead attribution, source performance, and pipeline analytics.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={period} onChange={e => setPeriod(Number(e.target.value))} style={{ width: 'auto', padding: '6px 12px', fontSize: 13 }}>
            <option value={30}>Last 30 Days</option>
            <option value={60}>Last 60 Days</option>
            <option value={90}>Last 90 Days</option>
            <option value={180}>Last 6 Months</option>
            <option value={365}>Last Year</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={fetchAttribution} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {loading ? 'Refreshing...' : '↻ Refresh'}
          </button>
          <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(74,124,89,0.12)', color: 'var(--accent-green)', padding: '4px 10px', borderRadius: 99, border: '1px solid rgba(74,124,89,0.25)' }}>
            ● Live Data
          </span>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 26, flexWrap: 'wrap', borderBottom: '2px solid var(--stone-200)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none',
            borderBottom: activeTab === t.id ? '2px solid var(--warm-gold)' : '2px solid transparent',
            marginBottom: -2, background: 'transparent', fontSize: 13,
            fontWeight: activeTab === t.id ? 700 : 500,
            color: activeTab === t.id ? 'var(--warm-gold)' : 'var(--stone-500)',
            cursor: 'pointer', borderRadius: '4px 4px 0 0',
          }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: 'rgba(192,97,74,0.1)', border: '1px solid rgba(192,97,74,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent-red)' }}>
          ⚠️ Error loading data: {error}
        </div>
      )}

      {/* ════════ OVERVIEW ════════ */}
      {activeTab === 'overview' && (
        <div className="fade-in">
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Total Leads', value: funnel.totalLeads || 0, sub: `${funnel.periodLeads || 0} this period`, icon: '📋', color: 'var(--accent-blue)' },
              { label: 'Conversion Rate', value: pct(funnel.overallConversion), sub: `${funnel.totalWon || 0} won / ${funnel.totalLost || 0} lost`, icon: '🎯', color: 'var(--accent-green)' },
              { label: 'Pipeline Value', value: fmt(funnel.pipelineValue), sub: 'Active opportunities', icon: '💰', color: 'var(--warm-gold)' },
              { label: 'Won Revenue', value: fmt(funnel.wonRevenue), sub: 'From converted leads', icon: '🏆', color: 'var(--accent-green)' },
              { label: 'Paid Revenue', value: fmt(funnel.totalPaidRevenue), sub: 'Collected to date', icon: '💵', color: '#2d6a4f' },
              { label: 'Auto-Created', value: funnel.autoCreatedLeads || 0, sub: `${funnel.autoCreatedPeriod || 0} this period`, icon: '🤖', color: 'var(--accent-blue)' },
            ].map(k => (
              <div key={k.label} className="card" style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--stone-400)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{k.label}</div>
                  <span style={{ fontSize: 16 }}>{k.icon}</span>
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 600, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Source Performance Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div className="card">
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Lead Sources</div>
              {sources.slice(0, 6).map(s => {
                const color = SOURCE_COLORS[s.source] || '#8c7b6e';
                const widthPct = sources[0]?.totalLeads > 0 ? (s.totalLeads / sources[0].totalLeads * 100) : 0;
                return (
                  <div key={s.source} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{s.source}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{s.totalLeads} leads</span>
                        <span style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{s.conversionRate}%</span>
                      </div>
                    </div>
                    <div style={{ height: 6, background: 'var(--stone-200)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${widthPct}%`, background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card">
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Revenue by Source</div>
              {Object.entries(revenue).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--stone-400)', fontSize: 13 }}>No paid invoices yet</div>
              ) : Object.entries(revenue).sort((a, b) => b[1].revenue - a[1].revenue).map(([src, data]) => (
                <div key={src} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--stone-200)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: SOURCE_COLORS[src] || '#8c7b6e' }} />
                    <span style={{ fontSize: 13 }}>{src}</span>
                    <span className="text-muted" style={{ fontSize: 11 }}>({data.paidJobs} job{data.paidJobs !== 1 ? 's' : ''})</span>
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--accent-green)' }}>{fmt(data.revenue)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Estimates Breakdown */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Estimate Pipeline</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {Object.entries(funnel.estimates || {}).map(([status, count]) => {
                const colors = { Sent: '#946c00', Draft: 'var(--stone-400)', Approved: 'var(--accent-green)', Expired: 'var(--accent-red)' };
                return (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--stone-100)', borderRadius: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors[status] || 'var(--stone-400)' }} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{status}</span>
                    <span style={{ fontSize: 18, fontFamily: 'var(--font-display)', fontWeight: 700 }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ════════ SOURCES ════════ */}
      {activeTab === 'sources' && (
        <div className="fade-in">
          <div className="card" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Source</th><th>Leads</th><th>Won</th><th>Lost</th><th>Quoted</th>
                  <th>Conversion</th><th>Avg Value</th><th>Total Value</th><th>Auto-Created</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => (
                  <tr key={s.source}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: SOURCE_COLORS[s.source] || '#8c7b6e' }} />
                        <span style={{ fontWeight: 600 }}>{s.source}</span>
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{s.totalLeads}</td>
                    <td style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{s.won}</td>
                    <td style={{ color: 'var(--accent-red)' }}>{s.lost}</td>
                    <td>{s.quoted}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: s.conversionRate >= 20 ? 'var(--accent-green)' : s.conversionRate >= 10 ? 'var(--warm-gold)' : 'var(--stone-500)' }}>
                        {s.conversionRate}%
                      </span>
                    </td>
                    <td>{fmt(s.avgValue)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(s.totalValue)}</td>
                    <td>
                      {s.autoCreated > 0 ? (
                        <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(107,140,174,0.15)', color: 'var(--accent-blue)', padding: '2px 8px', borderRadius: 99 }}>
                          🤖 {s.autoCreated}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Call Source Tracking */}
          {Object.keys(calls).length > 0 && (
            <div className="card" style={{ marginTop: 20 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Call Source Tracking</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {Object.entries(calls).map(([src, data]) => (
                  <div key={src} style={{ background: 'var(--stone-100)', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{src}</div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                      <div><span style={{ fontWeight: 700 }}>{data.total}</span> <span style={{ color: 'var(--stone-400)' }}>total</span></div>
                      <div><span style={{ fontWeight: 700, color: 'var(--accent-green)' }}>{data.answered}</span> <span style={{ color: 'var(--stone-400)' }}>answered</span></div>
                      <div><span style={{ fontWeight: 700, color: 'var(--accent-red)' }}>{data.missed}</span> <span style={{ color: 'var(--stone-400)' }}>missed</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════ TRENDS ════════ */}
      {activeTab === 'trends' && (
        <div className="fade-in">
          <div className="card">
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Weekly Lead Trend (12 Weeks)</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200, padding: '0 4px' }}>
              {trends.map((w, i) => {
                const maxTotal = Math.max(...trends.map(t => t.total), 1);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                    {w.total > 0 && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--stone-500)', marginBottom: 4 }}>{w.total}</div>
                    )}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {w.google > 0 && <div style={{ height: (w.google / maxTotal) * 160, background: '#4285f4', borderRadius: '2px 2px 0 0', minHeight: 2 }} />}
                      {w.facebook > 0 && <div style={{ height: (w.facebook / maxTotal) * 160, background: '#1877f2', minHeight: 2 }} />}
                      {w.referral > 0 && <div style={{ height: (w.referral / maxTotal) * 160, background: '#c9a96e', minHeight: 2 }} />}
                      {w.website > 0 && <div style={{ height: (w.website / maxTotal) * 160, background: '#4a7c59', minHeight: 2 }} />}
                      {w.direct > 0 && <div style={{ height: (w.direct / maxTotal) * 160, background: '#6b8cae', borderRadius: '0 0 2px 2px', minHeight: 2 }} />}
                      {w.total === 0 && <div style={{ height: 2, background: 'var(--stone-200)', borderRadius: 2 }} />}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--stone-400)', whiteSpace: 'nowrap', marginTop: 6, transform: 'rotate(-35deg)', transformOrigin: 'top center' }}>{w.week}</div>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Google', color: '#4285f4' },
                { label: 'Facebook/IG', color: '#1877f2' },
                { label: 'Referral', color: '#c9a96e' },
                { label: 'Website', color: '#4a7c59' },
                { label: 'Direct', color: '#6b8cae' },
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--stone-500)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════════ PROJECTS ════════ */}
      {activeTab === 'projects' && (
        <div className="fade-in">
          <div className="card" style={{ padding: 0 }}>
            <table>
              <thead><tr><th>Project Type</th><th>Leads</th><th>Won</th><th>Avg Value</th><th>Total Value</th></tr></thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.name}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.count}</td>
                    <td style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{p.won}</td>
                    <td>{fmt(p.avgValue)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════ GOOGLE ADS ════════ */}
      {activeTab === 'google' && (
        <div className="fade-in">
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#4285f418', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🔍</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Google Ads</div>
                <div style={{ fontSize: 12, color: googleStatus?.connected ? 'var(--accent-green)' : 'var(--stone-400)', fontWeight: 600 }}>
                  {googleStatus?.connected ? '● Connected' : googleStatus?.configured ? '○ Configured — not connected' : '○ Not configured'}
                </div>
              </div>
            </div>

            {googleStatus?.connected ? (
              <div>
                <p style={{ fontSize: 13, color: 'var(--stone-500)', marginBottom: 16 }}>
                  Your Google Ads account is connected. Campaign data will appear here when you have active campaigns running.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={async () => {
                    try {
                      const res = await fetch(`${BASE_URL}/api/marketing/google-ads?action=campaigns`);
                      const data = await res.json();
                      alert(data.campaigns?.length ? `Found ${data.campaigns.length} campaigns` : 'No campaign data — resume your campaigns to see data here.');
                    } catch (e) {
                      alert('Error fetching campaigns: ' + e.message);
                    }
                  }}>
                    Fetch Campaign Data
                  </button>
                </div>
              </div>
            ) : googleStatus?.configured ? (
              <div>
                <p style={{ fontSize: 13, color: 'var(--stone-500)', marginBottom: 16 }}>
                  API credentials are configured. Click below to authorize the connection.
                </p>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  const res = await fetch(`${BASE_URL}/api/marketing/google-ads?action=auth-url`);
                  const data = await res.json();
                  if (data.authUrl) window.location.href = data.authUrl;
                }}>
                  Connect Google Ads
                </button>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: 'var(--stone-500)', marginBottom: 12 }}>
                  Google Ads is not configured yet. Add the following environment variables to Vercel:
                </p>
                <div style={{ background: 'var(--stone-100)', borderRadius: 8, padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: 'var(--stone-600)' }}>
                  GOOGLE_ADS_CLIENT_ID<br/>
                  GOOGLE_ADS_CLIENT_SECRET<br/>
                  GOOGLE_ADS_DEVELOPER_TOKEN<br/>
                  GOOGLE_ADS_CUSTOMER_ID<br/>
                  GOOGLE_ADS_REDIRECT_URI
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* GOOGLE BUSINESS PROFILE TAB                                */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'gbp' && (
        <div className="fade-in">
          {/* Connection Header */}
          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#34a85318', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📍</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Google Business Profile</div>
                  <div style={{ fontSize: 12, color: gbpStatus?.connected ? 'var(--accent-green)' : 'var(--stone-400)', fontWeight: 600 }}>
                    {gbpStatus?.connected ? `● Connected${gbpStatus.locationName ? ' — ' + gbpStatus.locationName : ''}` : gbpStatus?.configured ? '○ Configured — not connected' : '○ Not configured'}
                  </div>
                </div>
              </div>
              {gbpStatus?.connected && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { fetchGBPReviews(); fetchGBPInsights(); fetchGBPPosts(); }}>
                    {gbpLoading ? 'Syncing...' : '↻ Sync Now'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {!gbpStatus?.connected ? (
            <div className="card" style={{ padding: 24 }}>
              {gbpStatus?.configured ? (
                <div>
                  <p style={{ fontSize: 13, color: 'var(--stone-500)', marginBottom: 16 }}>
                    API credentials are configured. Click below to authorize access to your Google Business Profile.
                  </p>
                  <button className="btn btn-primary btn-sm" onClick={handleGBPConnect}>
                    Connect Google Business Profile
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 13, color: 'var(--stone-500)', marginBottom: 12 }}>
                    Connect your Google Business Profile to auto-post project photos, sync reviews, reply to customers, and track profile performance — all from your CRM.
                  </p>
                  <div style={{ background: 'var(--stone-100)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--stone-700)' }}>Setup steps:</div>
                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--stone-600)', lineHeight: 2 }}>
                      1. Google Cloud Console → Enable "My Business" APIs<br/>
                      2. Create OAuth 2.0 credentials (Web Application)<br/>
                      3. Add these env vars to Vercel:<br/>
                      <span style={{ paddingLeft: 16, display: 'inline-block' }}>
                        GBP_CLIENT_ID<br/>
                        GBP_CLIENT_SECRET<br/>
                        GBP_REDIRECT_URI
                      </span>
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--stone-400)' }}>
                    Tip: You can reuse the same Google Cloud project as Google Ads. The redirect URI should be: <code style={{ background: 'var(--stone-100)', padding: '2px 6px', borderRadius: 4 }}>{BASE_URL}/api/marketing/gbp?action=callback</code>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── GBP Stats Row ─────────────────────────────── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--warm-gold)' }}>
                    {gbpStatus.avgRating ? Number(gbpStatus.avgRating).toFixed(1) : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Avg Rating</div>
                  <div style={{ fontSize: 16, letterSpacing: -1, marginTop: 2 }}>
                    {gbpStatus.avgRating ? '★'.repeat(Math.round(gbpStatus.avgRating)) + '☆'.repeat(5 - Math.round(gbpStatus.avgRating)) : '—'}
                  </div>
                </div>
                <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>{gbpStatus.reviewCount || 0}</div>
                  <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Total Reviews</div>
                </div>
                {gbpInsights?.metrics && (
                  <>
                    <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent-blue)' }}>{gbpInsights.metrics.searchImpressions || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Search Views</div>
                    </div>
                    <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent-green)' }}>{gbpInsights.metrics.websiteClicks || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Website Clicks</div>
                    </div>
                    <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#4285f4' }}>{gbpInsights.metrics.callClicks || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Call Clicks</div>
                    </div>
                    <div className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent-amber)' }}>{gbpInsights.metrics.directionRequests || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>Directions</div>
                    </div>
                  </>
                )}
              </div>

              {/* ── GBP Sub-tabs ─────────────────────────────── */}
              {(() => {
                const GBP_TABS = [
                  { id: 'reviews', label: 'Reviews', count: gbpReviews?.totalReviewCount },
                  { id: 'posts', label: 'Posts', count: gbpPosts?.posts?.length },
                  { id: 'insights', label: 'Insights' },
                ];

                return (
                  <>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--stone-200)', paddingBottom: 0 }}>
                      {GBP_TABS.map(t => (
                        <button key={t.id} onClick={() => setGbpTab(t.id)} style={{
                          padding: '8px 16px', fontSize: 13, fontWeight: gbpTab === t.id ? 600 : 400,
                          color: gbpTab === t.id ? 'var(--warm-gold)' : 'var(--stone-500)',
                          borderBottom: gbpTab === t.id ? '2px solid var(--warm-gold)' : '2px solid transparent',
                          background: 'none', border: 'none', borderBottomStyle: 'solid', cursor: 'pointer',
                          marginBottom: -1, transition: 'all 0.15s',
                        }}>
                          {t.label}{t.count != null ? ` (${t.count})` : ''}
                        </button>
                      ))}
                    </div>

                    {/* ── REVIEWS SUB-TAB ──────────────────────── */}
                    {gbpTab === 'reviews' && (
                      <div>
                        {gbpLoading && !gbpReviews ? (
                          <div style={{ textAlign: 'center', padding: 40, color: 'var(--stone-400)', fontSize: 13 }}>Loading reviews...</div>
                        ) : !gbpReviews?.reviews?.length ? (
                          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
                            <div style={{ fontSize: 14, color: 'var(--stone-500)' }}>No reviews synced yet. Click "Sync Now" to pull reviews from Google.</div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {gbpReviews.reviews.map((review, idx) => {
                              const rid = review.reviewId;
                              const draft = replyDraft[rid] || '';
                              const isGenerating = generatingReply === rid;

                              return (
                                <div key={rid || idx} className="card" style={{ padding: '16px 20px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      {review.profilePhotoUrl ? (
                                        <img src={review.profilePhotoUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                                      ) : (
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--stone-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: 'var(--stone-500)' }}>
                                          {(review.reviewer || '?')[0]}
                                        </div>
                                      )}
                                      <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{review.reviewer}</div>
                                        <div style={{ fontSize: 11, color: 'var(--stone-400)' }}>
                                          {review.createTime ? new Date(review.createTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 14, letterSpacing: -1, color: review.rating >= 4 ? 'var(--warm-gold)' : review.rating >= 3 ? 'var(--accent-amber)' : '#c92a2a' }}>
                                      {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                                    </div>
                                  </div>

                                  {review.comment && (
                                    <div style={{ fontSize: 13, color: 'var(--stone-600)', lineHeight: 1.6, marginBottom: review.reply ? 12 : 8, paddingLeft: 42 }}>
                                      "{review.comment}"
                                    </div>
                                  )}

                                  {/* Existing reply */}
                                  {review.reply && (
                                    <div style={{ marginLeft: 42, background: 'var(--stone-100)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, borderLeft: '3px solid var(--warm-gold)' }}>
                                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--warm-gold)', marginBottom: 4 }}>Your Reply</div>
                                      <div style={{ fontSize: 12, color: 'var(--stone-600)', lineHeight: 1.5 }}>{review.reply.comment || review.reply}</div>
                                    </div>
                                  )}

                                  {/* Reply actions (only if no existing reply) */}
                                  {!review.reply && (
                                    <div style={{ paddingLeft: 42 }}>
                                      {draft ? (
                                        <div style={{ marginTop: 8 }}>
                                          <textarea
                                            value={draft}
                                            onChange={e => setReplyDraft(prev => ({ ...prev, [rid]: e.target.value }))}
                                            style={{ width: '100%', minHeight: 70, fontSize: 12, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--stone-300)', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                                          />
                                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button className="btn btn-primary btn-sm" style={{ fontSize: 12 }} onClick={() => submitReviewReply(rid, draft)}>
                                              Post Reply to Google
                                            </button>
                                            <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => generateReviewReply(review)}>
                                              {isGenerating ? 'Regenerating...' : '↻ Regenerate'}
                                            </button>
                                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setReplyDraft(prev => { const n = { ...prev }; delete n[rid]; return n; })}>
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                          <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => generateReviewReply(review)}>
                                            {isGenerating ? 'Generating...' : '✨ Draft AI Reply'}
                                          </button>
                                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setReplyDraft(prev => ({ ...prev, [rid]: '' }))}>
                                            Write Reply
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── POSTS SUB-TAB ────────────────────────── */}
                    {gbpTab === 'posts' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                          <div style={{ fontSize: 13, color: 'var(--stone-500)' }}>
                            Auto-posts are created when jobs are marked Completed. Posts sync every 2 hours.
                          </div>
                          <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap', border: '1.5px solid var(--warm-gold)', color: 'var(--warm-gold)' }} onClick={async (e) => {
                            const btn = e.currentTarget;
                            btn.disabled = true; btn.textContent = 'Running...';
                            try {
                              const res = await fetch(`${BASE_URL}/api/cron/gbp-sync?force=true`);
                              const data = await res.json();
                              if (data.postsCreated > 0) {
                                btn.textContent = `✓ ${data.postsCreated} post${data.postsCreated > 1 ? 's' : ''} created`;
                                btn.style.borderColor = 'var(--accent-green)'; btn.style.color = 'var(--accent-green)';
                                fetchGBPPosts(); // Refresh the posts list
                              } else if (data.errors?.length) {
                                btn.textContent = '⚠ Error — check logs';
                                btn.style.borderColor = 'var(--accent-red)'; btn.style.color = 'var(--accent-red)';
                              } else {
                                btn.textContent = 'No eligible jobs';
                                btn.style.color = 'var(--stone-400)';
                              }
                            } catch (err) {
                              btn.textContent = '⚠ Failed';
                              btn.style.borderColor = 'var(--accent-red)'; btn.style.color = 'var(--accent-red)';
                            }
                            setTimeout(() => { btn.disabled = false; btn.textContent = '▶ Run Auto-Post Now'; btn.style.borderColor = ''; btn.style.color = ''; }, 5000);
                          }}>
                            ▶ Run Auto-Post Now
                          </button>
                        </div>

                        {!gbpPosts?.posts?.length ? (
                          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
                            <div style={{ fontSize: 14, color: 'var(--stone-500)', marginBottom: 4 }}>No GBP posts yet</div>
                            <div style={{ fontSize: 12, color: 'var(--stone-400)', lineHeight: 1.7, maxWidth: 400, margin: '0 auto' }}>
                              Completed jobs with "After" tagged photos are auto-posted to your Google Business Profile.
                              Make sure your photos are tagged, then click <strong>Run Auto-Post Now</strong> above.
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gap: 12 }}>
                            {gbpPosts.posts.map((post, idx) => (
                              <div key={idx} className="card" style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className={`badge badge-${post.state === 'LIVE' ? 'completed' : 'new'}`} style={{ fontSize: 11 }}>
                                      {post.state === 'LIVE' ? 'Live' : post.state}
                                    </span>
                                    <span style={{ fontSize: 11, color: 'var(--stone-400)' }}>
                                      {post.topicType === 'STANDARD' ? 'Update' : post.topicType === 'OFFER' ? 'Offer' : post.topicType}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 11, color: 'var(--stone-400)' }}>
                                    {post.createTime ? new Date(post.createTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                  </div>
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--stone-600)', lineHeight: 1.6 }}>
                                  {post.summary}
                                </div>
                                {post.media?.length > 0 && (
                                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--stone-400)' }}>
                                    📷 {post.media.length} photo{post.media.length !== 1 ? 's' : ''} attached
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── INSIGHTS SUB-TAB ──────────────────────── */}
                    {gbpTab === 'insights' && (
                      <div>
                        {!gbpInsights?.metrics ? (
                          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                            <div style={{ fontSize: 14, color: 'var(--stone-500)', marginBottom: 8 }}>Loading performance data...</div>
                            <button className="btn btn-secondary btn-sm" onClick={fetchGBPInsights}>Fetch Insights</button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                              {[
                                { label: 'Search impressions', value: gbpInsights.metrics.searchImpressions, color: 'var(--accent-blue)', sub: `${gbpInsights.metrics.desktopImpressions || 0} desktop · ${gbpInsights.metrics.mobileImpressions || 0} mobile` },
                                { label: 'Website clicks', value: gbpInsights.metrics.websiteClicks, color: 'var(--accent-green)' },
                                { label: 'Phone call clicks', value: gbpInsights.metrics.callClicks, color: '#4285f4' },
                                { label: 'Direction requests', value: gbpInsights.metrics.directionRequests, color: 'var(--accent-amber)' },
                              ].map((metric, idx) => (
                                <div key={idx} className="card" style={{ padding: '20px 24px' }}>
                                  <div style={{ fontSize: 32, fontWeight: 700, color: metric.color }}>{metric.value || 0}</div>
                                  <div style={{ fontSize: 13, color: 'var(--stone-500)', fontWeight: 500, marginTop: 4 }}>{metric.label}</div>
                                  {metric.sub && <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>{metric.sub}</div>}
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--stone-400)', textAlign: 'right' }}>
                              Last {period} days · via Google Business Profile Performance API
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketingDashboard;
